import { Stack, Typography, useTheme } from "@mui/material";
import { type Control, useWatch } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CoDebtorsSection } from "web_myplann/Portfolio/Forms/CoDebtors/CoDebtorsSection";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type {
	LeasingFormInput,
	LeasingFormOutput,
} from "web_myplann/Portfolio/Forms/LeasingForm/leasingFormSchema";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { useLoanPaymentFrequencyOptions } from "web_myplann/Portfolio/Hooks/useLoanPaymentFrequencyOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { ExtendedParamsDisclosure } from "web_myplann/Shared/Components/ExtendedParamsDisclosure";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowHint } from "web_myplann/Shared/Components/FlatRow/FlatRowHint";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";

type LeasingFormFieldsProps = {
	control: Control<LeasingFormInput, unknown, LeasingFormOutput>;
	collapseParams?: boolean;
	showRemainsToPay?: boolean;
	namePlaceholder?: string;
	contractNumberPlaceholder?: string;
	disableKeyFields?: boolean;
};

export const LeasingFormFields = ({
	control,
	collapseParams = false,
	showRemainsToPay = false,
	namePlaceholder,
	contractNumberPlaceholder,
	disableKeyFields = false,
}: LeasingFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const paymentFrequencyOptions = useLoanPaymentFrequencyOptions();

	const signedAt = useWatch({ control, name: "signedAt" });

	const {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	} = useProducerProductOptions(control, ClientPortfolioDomainEnum.Leasing);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddLeasing.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddLeasing.Fields.name)}
					placeholder={
						namePlaceholder ?? t(Resources.Portfolio.AddLeasing.namePlaceholder)
					}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddLeasing.Fields.contractNumber)}
					placeholder={
						contractNumberPlaceholder ??
						t(Resources.Portfolio.AddShared.Placeholders.fill)
					}
					disabled={disableKeyFields}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddLeasing.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
					isLoading={isLoadingProducers}
					disabled={disableKeyFields}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddLeasing.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
					disabled={disableKeyFields}
					isLast={!disableKeyFields}
				/>
				{disableKeyFields && (
					<FlatRowHint
						text={t(Resources.Portfolio.Lock.keyFieldsHint)}
						isLast
					/>
				)}
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddLeasing.Fields,
				)}
			/>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddLeasing.Sections.values)}
			</Typography>
			<ExtendedParamsDisclosure
				collapsible={collapseParams}
				showLabel={t(Resources.Portfolio.AddLeasing.showParameters)}
				hideLabel={t(Resources.Portfolio.AddLeasing.hideParameters)}
			>
				<Stack bgcolor={theme.palette.background.paper}>
					{showRemainsToPay && (
						<FlatRowFormInput
							control={control}
							name="value"
							type="float"
							max={ValidationLimits.Amount.Max}
							label={t(Resources.Portfolio.AddLeasing.Fields.remainsToPay)}
							placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
							suffix="Kč"
						/>
					)}
					<FlatRowFormInput
						control={control}
						name="originalObligationAmount"
						type="float"
						max={ValidationLimits.Amount.Max}
						label={t(
							Resources.Portfolio.AddLeasing.Fields.originalObligationAmount,
						)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormInput
						control={control}
						name="installment"
						type="float"
						max={ValidationLimits.Amount.Max}
						label={t(Resources.Portfolio.AddLeasing.Fields.installment)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormSelect
						control={control}
						name="paymentFrequency"
						label={t(Resources.Portfolio.AddLeasing.Fields.paymentFrequency)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
						options={paymentFrequencyOptions}
					/>
					<FlatRowFormInput
						control={control}
						name="interestRate"
						type="float"
						max={ValidationLimits.Amount.MaxInterestRate}
						label={t(Resources.Portfolio.AddLeasing.Fields.interestRate)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="% p.a."
						isLast
					/>
				</Stack>
			</ExtendedParamsDisclosure>

			<CoDebtorsSection
				control={control}
				sectionLabel={t(Resources.Portfolio.AddLeasing.Sections.coDebtors)}
			/>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddLeasing.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddLeasing.Fields.signedAt)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddLeasing.Fields.startAt)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddLeasing.Fields.endAt)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					minDate={signedAt ?? undefined}
					displayFormat={DateFormat.Display}
					disabled={disableKeyFields}
					isLast
				/>
			</Stack>
		</>
	);
};
